import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';

import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';

import { CollegeInfo, SchoolInfo, CollegeTermInfo } from '../../application/application.service';
import { ReportInfo } from '../../reports/reports.service';

@Component({
    selector: 'dialog-reportoptions-component',
    styleUrls: ['reportoptions.dialogcomponent.css'],
    templateUrl: 'reportoptions.dialogcomponent.html',
})
export class ReportOptionsDialogComponent implements OnInit, OnDestroy {
    private _subrelease: Subject<void> = new Subject<void>();

    AvailableColleges: CollegeInfo[];
    AvailableSchools: SchoolInfo[];
    canceltext: string;
    defaultoptions: BaseReportRunOptions;
    message: string;
    oktext: string;
    ReportInfo: ReportInfo;
    ReportOptionsForm: FormGroup;
    title: string;

    get ReportCollege() {
        return this.ReportOptionsForm && this.ReportOptionsForm.get('ReportCollege');
    }
    get ReportTerm() {
        return this.ReportOptionsForm && this.ReportOptionsForm.get('ReportTerm');
    }

    constructor(
        public dialogRef: MatDialogRef<ReportOptionsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: BaseReportOptionsDialogOptions,
        private fb: FormBuilder
    ) {
        this.AvailableColleges = data.AvailableColleges || [];
        this.AvailableSchools = data.AvailableSchools || [];
        this.canceltext = data.CancelText || 'Cancel';
        this.defaultoptions = data.DefaultOptions;
        this.message = data.MessageText || '';
        this.oktext = data.OKText || 'Run Report';
        this.ReportInfo = data.ReportInfo;
        this.title = data.TitleText || 'Choose Report Options';
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;

        // Create report form. If we were provided default options, we can use those to preselect things.
        self.ReportOptionsForm = this.fb.group({
            ReportCollege: [null, Validators.required],
            ReportSchool: [null],
            ReportTerm: [null, Validators.required],
        });

        const patchVals = {
            ReportSchool: null,
            ReportCollege: null,
            ReportTerm: null,
        };

        if (self.defaultoptions) {
            // Have a school?
            if (self.defaultoptions && self.defaultoptions.ReportSchool) {
                // Get the school entry and patch it in.
                const chosenSchool = self.AvailableSchools.find(item => {
                    return item.SchoolNumber === self.defaultoptions.ReportSchool.SchoolNumber;
                });

                if (chosenSchool) {
                    patchVals.ReportSchool = chosenSchool;
                }
            }

            // Have a college?
            if (self.defaultoptions && self.defaultoptions.ReportCollege) {
                // Get the college entry and patch it.
                const chosenCollege = self.AvailableColleges.find(item => {
                    return item.CollegeID === self.defaultoptions.ReportCollege.CollegeID;
                });

                if (chosenCollege) {
                    patchVals.ReportCollege = chosenCollege;
                }
            }

            // Have a term?
            if (self.defaultoptions && self.defaultoptions.ReportTerm && patchVals.ReportCollege) {
                // Get the term entry and patch it.
                const chosenTerm = (<CollegeInfo>patchVals.ReportCollege).Terms.find(item => {
                    return item.TermWindowID === self.defaultoptions.ReportTerm.TermWindowID;
                });

                if (chosenTerm) {
                    patchVals.ReportTerm = chosenTerm;
                }
            }
        }

        // Be kind: If the user can set schools, but there's only one available school, pre-select it.
        if (self.ReportInfo.CanSetSchool === true && (self.AvailableSchools && self.AvailableSchools.length === 1)) {
            patchVals.ReportSchool = self.AvailableSchools[0];
        }

        // Patch in default values
        self.ReportOptionsForm.patchValue(patchVals, { emitEvent: false });

        self.ReportOptionsForm.updateValueAndValidity({ emitEvent: false });

        // If we didn't patch in a college, disable the term by default.
        if (!patchVals.ReportCollege) {
            self.ReportOptionsForm.get('ReportTerm').disable();
        }

        // Listen for form changes, so we can enable the term option upon a college selection
        self.ReportOptionsForm.valueChanges.pipe(takeUntil(self._subrelease)).subscribe(value => {
            if (value && value.ReportCollege) {
                self.ReportTerm.enable({ onlySelf: true, emitEvent: false });
            } else {
                self.ReportTerm.disable({ onlySelf: true, emitEvent: false });
            }
        });
    }

    // #region Cancel Handler Method

    CancelHandler() {
        // User cancelled.
        this.dialogRef.close(null);
    }

    // #endregion

    // #region OK Handler Method

    OkHandler() {
        // User chose the run the report. Return back the selected options
        const form = this.ReportOptionsForm.getRawValue();

        this.dialogRef.close(<BaseReportRunOptions>{
            ReportCollege: form.ReportCollege,
            ReportInfo: this.ReportInfo,
            ReportSchool: form.ReportSchool,
            ReportTerm: form.ReportTerm,
        });
    }

    // #endregion
}

// #region Custom Classes

// #region Report Options Dialog Options Class

export class BaseReportOptionsDialogOptions {
    TitleText?: string;
    MessageText?: string;
    OKText?: string;
    CancelText?: string;
    IconColor?: string;
    IconName?: string;
    ShowIcon?: boolean;
    height?: string;
    width?: string;
    AvailableColleges?: CollegeInfo[];
    AvailableSchools?: SchoolInfo[];
    DefaultOptions?: BaseReportRunOptions;
    ReportInfo?: ReportInfo;
}

// #endregion

// #region Report Run Options Class

export class BaseReportRunOptions {
    ReportInfo?: ReportInfo;
    ReportSchool?: SchoolInfo;
    ReportTerm?: CollegeTermInfo;
    ReportCollege?: CollegeInfo;
    StudentID?: string;
}

// #endregion

// #endregion



// WEBPACK FOOTER //
// ./src/services/dialogs/reportoptions/reportoptions.dialogcomponent.ts